import { apiClient } from './client'
import type {
  OrganizationDTO,
  UserDTO,
  UUID,
  ISODateTime,
} from './types'

/**
 * SaaS plug-and-play (STEP 9.0) — organization onboarding, settings,
 * members, invitations and vertical templates.
 *
 * Mirrors the FastAPI routers under ``rager/organizations/router.py``.
 * Keep this file in sync by hand — the project still has no codegen.
 */

export interface VerticalTemplateLabels {
  entity_label: string
  entity_placeholder: string
  group_label: string
  group_placeholder: string
  document_title_label: string
  case_document_examples: string[]
  reference_document_examples: string[]
  reference_type_suggestions: string[]
  empty_state_helper: string
  upload_helper: string
}

export interface VerticalTemplateDTO {
  id: string
  name: string
  description: string
  labels: VerticalTemplateLabels
}

export interface TemplatesListResponseDTO {
  items: VerticalTemplateDTO[]
  total: number
}

export interface WorkspaceSettingsDTO {
  id: UUID
  organization_id: UUID
  vertical_template: string
  default_language: string
  default_timezone: string
  metadata_labels_json: Record<string, unknown> | null
  rag_defaults_json: Record<string, unknown> | null
  feature_flags_json: Record<string, unknown> | null
  created_at: ISODateTime
  updated_at: ISODateTime
}

export interface OrganizationSettingsResponseDTO {
  organization: OrganizationDTO
  settings: WorkspaceSettingsDTO
  template: VerticalTemplateDTO
}

export interface MemberItemDTO {
  id: UUID
  organization_id: UUID
  user_id: UUID
  role: string
  status: string
  email: string
  full_name: string
  display_name: string | null
  invited_at: ISODateTime | null
  accepted_at: ISODateTime | null
  removed_at: ISODateTime | null
  created_at: ISODateTime
  updated_at: ISODateTime
}

export interface MembersListResponseDTO {
  organization_id: UUID
  total: number
  items: MemberItemDTO[]
}

export interface InvitationDTO {
  id: UUID
  organization_id: UUID
  tenant_id: string
  email: string
  role: string
  expires_at: ISODateTime
  created_by_user_id: UUID | null
  accepted_at: ISODateTime | null
  accepted_by_user_id: UUID | null
  revoked_at: ISODateTime | null
  revoked_by_user_id: UUID | null
  created_at: ISODateTime
  updated_at: ISODateTime
}

export interface InvitationCreateResponseDTO {
  invitation: InvitationDTO
  token: string
  accept_path: string
}

export interface InvitationsListResponseDTO {
  organization_id: UUID
  total: number
  items: InvitationDTO[]
}

export interface InvitationAcceptResponseDTO {
  organization: OrganizationDTO
  user: UserDTO
  role: string
  created_user: boolean
  created_membership: boolean
  requires_login: boolean
}

export interface OnboardOrganizationPayload {
  name: string
  slug: string
  tenant_id?: string
  vertical_template: string
  language: string
  timezone: string
  legal_name?: string
  owner_email?: string
  owner_full_name?: string
}

export interface UpdateWorkspaceSettingsPayload {
  vertical_template?: string
  default_language?: string
  default_timezone?: string
  metadata_labels_json?: Record<string, unknown> | null
  rag_defaults_json?: Record<string, unknown> | null
  feature_flags_json?: Record<string, unknown> | null
}

export interface UpdateMemberPayload {
  role?: 'owner' | 'admin' | 'member' | 'viewer' | 'auditor'
  status?: 'active' | 'invited' | 'suspended' | 'removed'
}

export interface CreateInvitationPayload {
  email: string
  role: UpdateMemberPayload['role']
  ttl_hours?: number
}

export interface AcceptInvitationPayload {
  token: string
  full_name?: string
  password?: string
}

export const templatesApi = {
  list: () => apiClient.get<TemplatesListResponseDTO>('/api/v1/templates'),
}

export const organizationsApi = {
  /** Admin/bootstrap onboarding. Cookie identity OR admin key (when set). */
  create: (payload: OnboardOrganizationPayload) =>
    apiClient.post<OrganizationSettingsResponseDTO>(
      '/api/v1/organizations',
      payload,
    ),

  /** Self-service: current organization snapshot for the logged-in user. */
  getCurrent: () =>
    apiClient.get<OrganizationSettingsResponseDTO>(
      '/api/v1/organization/current',
    ),

  getCurrentSettings: () =>
    apiClient.get<OrganizationSettingsResponseDTO>(
      '/api/v1/organization/current/settings',
    ),

  updateCurrentSettings: (payload: UpdateWorkspaceSettingsPayload) =>
    apiClient.patch<OrganizationSettingsResponseDTO>(
      '/api/v1/organization/current/settings',
      payload,
    ),

  // Members
  listMembers: () =>
    apiClient.get<MembersListResponseDTO>(
      '/api/v1/organization/current/members',
    ),

  updateMember: (memberId: UUID, payload: UpdateMemberPayload) =>
    apiClient.patch<MemberItemDTO>(
      `/api/v1/organization/current/members/${memberId}`,
      payload,
    ),

  removeMember: (memberId: UUID) =>
    apiClient.del<MemberItemDTO>(
      `/api/v1/organization/current/members/${memberId}`,
    ),

  // Invitations
  listInvitations: () =>
    apiClient.get<InvitationsListResponseDTO>(
      '/api/v1/organization/current/invitations',
    ),

  createInvitation: (payload: CreateInvitationPayload) =>
    apiClient.post<InvitationCreateResponseDTO>(
      '/api/v1/organization/current/invitations',
      payload,
    ),

  revokeInvitation: (invitationId: UUID) =>
    apiClient.post<InvitationDTO>(
      `/api/v1/organization/current/invitations/${invitationId}/revoke`,
    ),
}

export const invitationsApi = {
  accept: (payload: AcceptInvitationPayload) =>
    apiClient.post<InvitationAcceptResponseDTO>(
      '/api/v1/auth/invitations/accept',
      payload,
      { skipAuthRefresh: true },
    ),
}
